import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { CpuIcon, ShieldIcon, EyeIcon, SlidersHorizontalIcon, RotateCcwIcon, AlertTriangleIcon, ArrowRightIcon } from 'lucide-react';
const PlatformFeature = ({
  icon,
  title,
  description,
  index
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true
}} transition={{
  duration: 0.5,
  delay: index * 0.08
}} className="flex items-start gap-3 sm:gap-4" data-id="element-805">
    <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gray-800 flex items-center justify-center text-orange-400 mt-0.5 border border-gray-700" data-id="element-806">
      {icon}
    </div>
    <div data-id="element-807">
      <h4 className="text-base sm:text-lg font-bold text-white mb-1" data-id="element-808">
        {title}
      </h4>
      <p className="text-gray-400 leading-relaxed text-sm" data-id="element-809">{description}</p>
    </div>
  </motion.div>;
const Platform = () => {
  const features = [{
    icon: <SlidersHorizontalIcon size={18} className="sm:w-5 sm:h-5" data-id="element-810" />,
    title: 'Operating envelope controls',
    description: 'Allowed actions, constraints, tool allowlists, and parameter bounds — defined per workflow, per user, per context.'
  }, {
    icon: <CpuIcon size={18} className="sm:w-5 sm:h-5" data-id="element-811" />,
    title: 'Orchestration runtime',
    description: 'Multi-step workflows, handoffs, retries, and fallbacks. The engine that turns intent into reliable execution.'
  }, {
    icon: <ShieldIcon size={18} className="sm:w-5 sm:h-5" data-id="element-812" />,
    title: 'Policy engine',
    description: 'Role-based permissions, approval gates, budget limits, and rate controls enforced at the platform level.'
  }, {
    icon: <EyeIcon size={18} className="sm:w-5 sm:h-5" data-id="element-813" />,
    title: 'Deterministic validation',
    description: 'Business rules, schema checks, and constraints enforced every time — regardless of which model runs.'
  }, {
    icon: <RotateCcwIcon size={18} className="sm:w-5 sm:h-5" data-id="element-814" />,
    title: 'Audit + replay',
    description: 'Trace every action, tool call, and decision. Reproduce any execution for debugging or compliance.'
  }, {
    icon: <AlertTriangleIcon size={18} className="sm:w-5 sm:h-5" data-id="element-815" />,
    title: 'Safe degrade',
    description: "Fall back to Assist mode instead of guessing. When confidence is low, the system asks — it doesn't improvise."
  }];
  return <section className="w-full bg-gray-900 py-16 sm:py-24 md:py-32 lg:py-48" data-id="element-816">
      <div className="container mx-auto px-4 sm:px-6 md:px-8" data-id="element-817">
        <div className="max-w-7xl mx-auto" data-id="element-818">
          <div className="flex flex-col lg:flex-row gap-12 sm:gap-14 md:gap-16 lg:gap-24" data-id="element-819">
            {/* Left: Messaging */}
            <div className="lg:w-1/2" data-id="element-820">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} data-id="element-821">
                <p className="text-orange-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-3 sm:mb-4" data-id="element-822">
                  The Platform
                </p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 tracking-tight leading-tight" data-id="element-823">
                  Components are the interface. The platform is the guarantee.
                </h2>
                <p className="text-lg sm:text-xl text-gray-400 mb-6 sm:mb-8 leading-relaxed" data-id="element-824">
                  FluidWeb combines agentic UI surfaces with a policy +
                  orchestration runtime so execution is constrained, observable,
                  and repeatable.
                </p>

                {/* Visual: Architecture diagram */}
                <div className="bg-gray-900 rounded-xl sm:rounded-2xl border border-gray-800 p-4 sm:p-6 md:p-8" data-id="element-825">
                  <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wider font-semibold mb-4 sm:mb-6" data-id="element-826">
                    Architecture
                  </div>
                  <div className="space-y-2 sm:space-y-3" data-id="element-827">
                    {/* FluidWeb Platform wrapper */}
                    <div className="rounded-lg sm:rounded-xl border border-orange-700/40 bg-orange-950/10 p-2 sm:p-3 space-y-2 sm:space-y-3" data-id="element-828">
                      <div className="text-[9px] sm:text-[10px] text-orange-400 font-bold uppercase tracking-widest px-1" data-id="element-829">
                        FluidWeb Platform
                      </div>
                      {/* UI Layer */}
                      <div className="bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-700" data-id="element-830">
                        <div className="text-[10px] sm:text-xs text-gray-400 mb-2 font-semibold" data-id="element-831">
                          UI Layer
                        </div>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-832">
                          {['Forms', 'Data Explorer', 'Navigation', 'Actions', 'Custom…'].map(c => <span key={c} className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-gray-700 text-gray-300 border border-gray-600" data-id="element-833">
                              {c}
                            </span>)}
                        </div>
                      </div>
                      {/* Arrow */}
                      <div className="flex justify-center" data-id="element-834">
                        <div className="w-px h-3 sm:h-4 bg-gray-700" data-id="element-835"></div>
                      </div>
                      {/* Orchestration Layer */}
                      <div className="bg-orange-950/40 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-orange-800/50" data-id="element-836">
                        <div className="text-[10px] sm:text-xs text-orange-400 mb-2 font-semibold" data-id="element-837">
                          Orchestration + Policy Runtime
                        </div>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-838">
                          {['Execution Engine', 'Policy Engine', 'Approvals', 'Audit Logs', 'Replay'].map(c => <span key={c} className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-orange-900/40 text-orange-300 border border-orange-800/40" data-id="element-839">
                              {c}
                            </span>)}
                        </div>
                      </div>
                    </div>
                    {/* Arrow */}
                    <div className="flex justify-center" data-id="element-840">
                      <div className="w-px h-3 sm:h-4 bg-gray-700" data-id="element-841"></div>
                    </div>
                    {/* Your Backend */}
                    <div className="bg-gray-800 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-gray-700" data-id="element-842">
                      <div className="text-[10px] sm:text-xs text-gray-400 mb-2 font-semibold" data-id="element-843">
                        Your Backend
                      </div>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-844">
                        {['APIs', 'Database', 'Auth', 'Business Logic'].map(c => <span key={c} className="text-[10px] sm:text-xs px-2 sm:px-3 py-1 sm:py-1.5 rounded-md sm:rounded-lg bg-gray-700 text-gray-300 border border-gray-600" data-id="element-845">
                              {c}
                            </span>)}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right: Feature list */}
            <div className="lg:w-1/2" data-id="element-846">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} data-id="element-847">
                <p className="text-gray-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-6 sm:mb-8" data-id="element-848">
                  What you get
                </p>
                <div className="space-y-5 sm:space-y-6" data-id="element-849">
                  {features.map((feature, index) => <PlatformFeature key={index} icon={feature.icon} title={feature.title} description={feature.description} index={index} data-id="element-850" />)}
                </div>

                <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-800" data-id="element-851">
                  <p className="text-gray-500 text-sm mb-4 sm:mb-6" data-id="element-852">
                    FluidWeb isn't a component library. It's the execution +
                    control plane that makes agentic UI production-safe.
                  </p>
                  <a href="https://cal.com/fluidn/30min" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-orange-400 font-semibold text-sm hover:text-orange-300 transition-colors min-h-[44px]" data-id="element-853">
                    Read the platform docs
                    <ArrowRightIcon size={16} data-id="element-854" />
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default Platform;